/**
 * buildOverviewAttributes
 *
 * Shared builder for the `attributesByName` dictionary that `AssetOverview`
 * consumes. Used by BOTH the asset drawer (`AssetDrawerContent`) and the
 * webapp seed drawer's overview tab (`WebApplicationOverviewTab`) — a webapp
 * seed and its Assets-table row are the SAME Neo4j node, so both drawers must
 * feed `AssetOverview` an identical attribute shape for the same node.
 *
 * Note on the cloud spreads: `isCloudResource` can be true via cloud DNS
 * patterns (e.g. `.amazonaws.com`, `.cloudfront.net` on `asset.dns`), not
 * just key prefixes — so the base cloud fields (arn/name/resourceType/region)
 * are keyed on `isCloudResource(asset)` alone, and the
 * `#awsresource#`/`#azureresource#`/`#gcpresource#` key prefixes only select
 * WHICH provider-account field (account/subscription/project) is populated.
 */

import { isCloudResource } from '@/sections/asset/components/tabs';
import { Asset } from '@/types';

import type { OverviewTabAttributeList } from './AttributesMapSection';

interface BuildOverviewAttributesParams {
  /** The asset (for a webapp seed: the seed's asset-side node) */
  asset: Asset;
  /** PTR attribute records — each call site resolves its own source */
  ptr: { value: string | number }[];
  /** Identifier of the drawer's entity (IP string when isIpAddress is true) */
  entityIdentifier: string | undefined;
  /** Whether entityIdentifier matched Regex.IPV4/IPV6 at the call site */
  isIpAddress: boolean;
}

export function buildOverviewAttributes({
  asset,
  ptr,
  entityIdentifier,
  isIpAddress,
}: BuildOverviewAttributesParams): Record<
  OverviewTabAttributeList,
  { value: string | number }[]
> {
  return {
    ptr,
    country: [{ value: asset.country }],
    asname: [{ value: asset.asname }],
    asnumber: [{ value: asset.asnumber }],
    asrange: [{ value: asset.asrange }],
    // Both call sites compute isIpAddress as
    // `Regex.IPV4.test(entityIdentifier || '') || Regex.IPV6.test(...)`, and
    // neither regex can match an empty string — so isIpAddress === true
    // implies entityIdentifier is a non-empty string and the `?? ''` never
    // fires. This single expression therefore reproduces both former
    // call-site behaviors exactly (asset drawer:
    // `isIp ? [{ value: entityIdentifier || '' }] : []`; seed overview tab:
    // `isIp && entityIdentifier ? [{ value: entityIdentifier }] : []`).
    ipaddress: isIpAddress ? [{ value: entityIdentifier ?? '' }] : [],
    arn: [],
    name: [],
    resourceType: [],
    region: [],
    account: [],
    subscription: [],
    project: [],
    ...(isCloudResource(asset)
      ? {
          arn: [{ value: asset.properties?.Arn }],
          name: [{ value: asset.name }],
          resourceType: [
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { value: (asset as any).resourceType },
          ],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          region: [{ value: (asset as any).region }],
        }
      : {}),
    ...(isCloudResource(asset) && asset.key?.startsWith('#awsresource#')
      ? {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          account: [{ value: (asset as any).accountRef }],
        }
      : {}),
    ...(isCloudResource(asset) && asset.key?.startsWith('#azureresource#')
      ? {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          subscription: [{ value: (asset as any).accountRef }],
        }
      : {}),
    ...(isCloudResource(asset) && asset.key?.startsWith('#gcpresource#')
      ? {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          project: [{ value: (asset as any).accountRef }],
        }
      : {}),
  };
}
